import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CarFront, AlertTriangle, CalendarCheck, Truck, RefreshCw, ListChecks,
} from "lucide-react";
import { toast } from "sonner";
import { useApp, usePerm } from "@/context/AppContext";
import { avDashboard } from "@/lib/api";
import { fmtDateTime, fmtDate } from "@/lib/format";
import { AV_STATUSES, AV_STATUS_TONES, statusLabel, AGEING_TONES } from "./avShared";
import AgeingBadge from "./AgeingBadge";

const STATUS_KPIS = [
  { key: "vehicle_received", color: "yamaha" },
  { key: "estimate_pending", color: "amber" },
  { key: "customer_approval_pending", color: "amber" },
  { key: "documents_pending", color: "amber" },
  { key: "parts_pending", color: "violet" },
  { key: "repair_in_progress", color: "yamaha" },
  { key: "billing_pending", color: "amber" },
  { key: "ready_delivery", color: "emerald" },
  { key: "on_hold", color: "rose" },
];

const AvDashboard = () => {
  const { currentLocation } = useApp();
  const canAdd = usePerm("av", "add");
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (currentLocation && currentLocation !== "ALL") params.location_code = currentLocation;
      setData(await avDashboard(params));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load AV dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentLocation]);

  const kpis = data?.kpis || {};
  const byStatus = kpis.by_status || {};
  const ageing = data?.ageing || { red: 0, yellow: 0, green: 0 };

  const goToWorkbench = (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    navigate(`/av/workbench${qs ? `?${qs}` : ""}`);
  };

  return (
    <div className="space-y-5" data-testid="av-dashboard">
      <header className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-yamaha-50 flex items-center justify-center">
          <CarFront className="w-6 h-6 text-yamaha-700" />
        </div>
        <div>
          <h1 className="text-[24px] font-semibold text-yamaha-800 leading-none tracking-tight">
            Accidental Vehicles
          </h1>
          <p className="text-[12.5px] text-[#747684] mt-1">
            Track accidental vehicles from intake to delivery. Click any card to drill into the workbench.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <button
            onClick={load}
            disabled={loading}
            data-testid="av-dashboard-refresh"
            className="text-[11.5px] inline-flex items-center gap-1 text-yamaha-700 hover:text-yamaha-800 font-semibold"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          {canAdd && (
          <button
            onClick={() => navigate("/av/new")}
            data-testid="av-dashboard-new-btn"
            className="px-4 py-2 rounded-lg bg-yamaha-700 hover:bg-yamaha-800 text-white text-[12.5px] font-semibold"
          >
            + New AV Entry
          </button>
          )}
        </div>
      </header>

      {/* Hero: ageing buckets + due today + active total */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <HeroCard
          accent="rose"
          label="High Ageing Vehicles"
          value={ageing.red}
          icon={AlertTriangle}
          subtitle="> 15 working days"
          onClick={() => goToWorkbench({ ageing: "red" })}
          testid="av-hero-red"
        />
        <HeroCard
          accent="amber"
          label="Medium Ageing"
          value={ageing.yellow}
          icon={AlertTriangle}
          subtitle="8 – 15 working days"
          onClick={() => goToWorkbench({ ageing: "yellow" })}
          testid="av-hero-yellow"
        />
        <HeroCard
          accent="emerald"
          label="Low Ageing"
          value={ageing.green}
          icon={AlertTriangle}
          subtitle="≤ 7 working days"
          onClick={() => goToWorkbench({ ageing: "green" })}
          testid="av-hero-green"
        />
        <HeroCard
          accent="violet"
          label="Likely Delivery Today / Tomorrow"
          value={(kpis.due_today || 0) + (kpis.due_tomorrow || 0)}
          icon={CalendarCheck}
          subtitle={`Today ${kpis.due_today || 0} · Tomorrow ${kpis.due_tomorrow || 0}`}
          onClick={() => goToWorkbench({ due: "soon" })}
          testid="av-hero-due"
        />
      </div>

      {/* Smaller status KPI tiles */}
      <div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-[#747684] mb-2">By Status</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiTile
            label="Total Active AV"
            value={kpis.total_active || 0}
            onClick={() => goToWorkbench()}
            testid="av-kpi-total-active"
            emphasis
          />
          {STATUS_KPIS.map((s) => (
            <KpiTile
              key={s.key}
              label={statusLabel(s.key)}
              value={byStatus[s.key] || 0}
              onClick={() => goToWorkbench({ status: s.key })}
              testid={`av-kpi-${s.key}`}
            />
          ))}
          <KpiTile
            label="Delivered This Month"
            value={kpis.delivered_this_month || 0}
            onClick={() => goToWorkbench({ status: "delivered" })}
            testid="av-kpi-delivered"
          />
        </div>
      </div>

      {/* Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <ListCard
          title="High Ageing (>15d)"
          accent="rose"
          rows={data?.high_ageing || []}
          emptyText="No high-ageing AV in selected location."
          renderRight={(r) => <AgeingBadge bucket={r.ageing_bucket} days={r.actual_working_days || 0} size="sm" />}
          onClickRow={(r) => navigate(`/av/${r.id}`)}
          testid="av-list-high-ageing"
        />
        <ListCard
          title="Ready for Delivery"
          accent="emerald"
          rows={data?.ready_delivery || []}
          emptyText="No vehicles ready for delivery."
          renderRight={(r) => (
            <span className="text-[11px] text-emerald-700 font-semibold">{fmtDate(r.likely_delivery_date)}</span>
          )}
          onClickRow={(r) => navigate(`/av/${r.id}`)}
          testid="av-list-ready"
        />
        <ListCard
          title="Today's Updates"
          accent="yamaha"
          rows={data?.today_updates || []}
          emptyText="No updates today."
          renderRight={(r) => (
            <span className="text-[10.5px] text-[#747684]">{fmtDateTime(r.updated_at)}</span>
          )}
          onClickRow={(r) => navigate(`/av/${r.id}`)}
          testid="av-list-today"
        />
      </div>
    </div>
  );
};

const HeroCard = ({ accent, label, value, icon: Icon, subtitle, onClick, testid }) => {
  const tones = {
    rose: "border-rose-200 bg-rose-50/60 text-rose-700",
    amber: "border-amber-200 bg-amber-50/60 text-amber-700",
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-700",
    violet: "border-violet-200 bg-violet-50/60 text-violet-700",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left border rounded-xl p-4 transition-all hover:shadow-md ${tones[accent]}`}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <div className="text-[11px] font-bold uppercase tracking-wider">{label}</div>
      </div>
      <div className="text-[32px] font-bold mt-1 leading-none">{value}</div>
      {subtitle && <div className="text-[11px] opacity-80 mt-2">{subtitle}</div>}
    </button>
  );
};

const KpiTile = ({ label, value, onClick, testid, emphasis }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`text-left bg-white border border-[#e2e1eb] rounded-xl p-3 hover:shadow-sm transition-shadow ${
      emphasis ? "ring-1 ring-yamaha-200" : ""
    }`}
  >
    <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#747684]">{label}</div>
    <div className={`mt-0.5 ${emphasis ? "text-[22px] text-yamaha-700" : "text-[20px] text-[#1a1b22]"} font-semibold`}>{value}</div>
  </button>
);

const ListCard = ({ title, accent, rows, emptyText, renderRight, onClickRow, testid }) => {
  const tone = AGEING_TONES[accent === "rose" ? "red" : accent === "amber" ? "yellow" : accent === "emerald" ? "green" : "green"];
  return (
    <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden" data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-[#e2e1eb] flex items-center justify-between">
        <div className="text-[13px] font-semibold text-[#1a1b22] flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${tone?.dot || "bg-yamaha-500"}`} />
          {title}
        </div>
        <div className="text-[11px] text-[#747684]">{rows.length}</div>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {rows.length === 0 && (
          <div className="px-4 py-8 text-center text-[#747684] text-[12px]">{emptyText}</div>
        )}
        {rows.map((r) => (
          <button
            key={r.id}
            onClick={() => onClickRow && onClickRow(r)}
            className="w-full text-left flex items-center gap-3 px-4 py-2 border-b border-[#f1f0f7] last:border-b-0 hover:bg-[#faf8ff] transition-colors"
          >
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-semibold text-[#1a1b22] truncate">{r.customer_name}</div>
              <div className="text-[10.5px] text-[#747684] truncate font-mono">{r.av_number} · {r.registration_number}</div>
            </div>
            <div className="flex-shrink-0">
              {renderRight && renderRight(r)}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default AvDashboard;
